import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Youtube,
  Mail,
  Send,
  Sparkles,
  Building2,
  Tag,
  Briefcase,
  CheckCircle,
  AlertCircle,
  BookOpen,
} from 'lucide-react';
import { theme, Button } from '../../design-system';
import { getCookie } from '../../utils/csrf';

// Pinterest icon component (not in lucide-react)
const Pinterest = ({ size = 24, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
  </svg>
);

// TikTok icon component (not in lucide-react)
const TikTok = ({ size = 24, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// ============ Styled Components ============

const FooterWrapper = styled.footer`
  background: ${theme.colors.neutral[900]};
  color: ${theme.colors.neutral[300]};
  /* Reserve minimum space to prevent CLS */
  min-height: 600px;
  contain: layout style;
`;

const NewsletterSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.primary[600]} 0%,
    ${theme.colors.secondary[500]} 100%
  );
  padding: ${theme.spacing[16]} ${theme.spacing[4]};
  min-height: 200px; /* Reserve space to prevent CLS */

  ${theme.media.md} {
    padding: ${theme.spacing[20]} ${theme.spacing[6]};
    min-height: 220px;
  }
`;

const NewsletterContainer = styled.div`
  max-width: ${theme.containerWidths.lg};
  margin: 0 auto;
  text-align: center;
`;

const NewsletterTitle = styled.h3`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.neutral[0]};
  margin-bottom: ${theme.spacing[2]};

  ${theme.media.md} {
    font-size: ${theme.fontSizes['3xl']};
  }
`;

const NewsletterSubtitle = styled.p`
  font-size: ${theme.fontSizes.base};
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: ${theme.spacing[8]};
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  ${theme.media.md} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  max-width: 500px;
  margin: 0 auto;

  ${theme.media.sm} {
    flex-direction: row;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  height: 52px;
  padding: 0 ${theme.spacing[4]};
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: ${theme.radii.xl};
  background: rgba(255, 255, 255, 0.1);
  color: ${theme.colors.neutral[0]};
  font-size: ${theme.fontSizes.base};
  outline: none;
  transition: ${theme.transitions.default};
  backdrop-filter: blur(10px);

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.5);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const NewsletterButton = styled(Button)`
  height: 52px;
  padding: 0 ${theme.spacing[6]};
  background: ${theme.colors.neutral[0]};
  color: ${theme.colors.primary[600]};
  border: none;
  font-weight: ${theme.fontWeights.semibold};

  &:hover {
    background: ${theme.colors.neutral[100]};
    transform: translateY(-2px);
  }

  ${theme.media.sm} {
    flex-shrink: 0;
  }
`;

const NewsletterMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.radii.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};

  ${({ $type }) =>
    $type === 'success'
      ? `
    background: rgba(16, 185, 129, 0.2);
    color: ${theme.colors.success[300]};
  `
      : `
    background: rgba(239, 68, 68, 0.2);
    color: ${theme.colors.error[300]};
  `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MainFooter = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.neutral[800]};
  min-height: 280px; /* Reserve space to prevent CLS */

  ${theme.media.md} {
    padding: ${theme.spacing[20]} ${theme.spacing[6]};
    min-height: 320px;
  }
`;

const FooterContainer = styled.div`
  max-width: ${theme.containerWidths['2xl']};
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing[10]};
  align-items: start;
  justify-items: start;

  ${theme.media.sm} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${theme.media.lg} {
    grid-template-columns: 2fr repeat(3, 1fr);
    gap: ${theme.spacing[12]};
  }
`;

const FooterBrand = styled.div`
  ${theme.media.sm} {
    grid-column: 1 / -1;
  }

  ${theme.media.lg} {
    grid-column: auto;
  }
`;

const FooterLogo = styled(Link)`
  display: inline-block;
  margin-bottom: ${theme.spacing[4]};
  /* Reserve exact dimensions to prevent CLS */
  width: 160px;
  height: 56px;

  img {
    height: 56px;
    width: 160px;
    object-fit: contain;
  }
`;

const BrandDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  line-height: ${theme.lineHeights.relaxed};
  color: ${theme.colors.neutral[400]};
  margin-bottom: ${theme.spacing[6]};
  max-width: 320px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.neutral[400]};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.primary[400]};
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.neutral[500]};
    flex-shrink: 0;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const ColumnTitle = styled.h4`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.neutral[0]};
  text-transform: uppercase;
  letter-spacing: ${theme.letterSpacings.wide};
  margin-bottom: ${theme.spacing[5]};
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: ${({ $horizontal }) => $horizontal ? 'row' : 'column'};
  flex-wrap: ${({ $horizontal }) => $horizontal ? 'wrap' : 'nowrap'};
  gap: ${({ $horizontal }) => $horizontal ? theme.spacing[4] : theme.spacing[3]};
  align-items: flex-start;
  width: 100%;
`;

const FooterLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.neutral[400]};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.primary[400]};
    transform: translateX(4px);
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`;

const FooterExternalLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.neutral[400]};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.primary[400]};
    transform: translateX(4px);
  }

  svg {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
`;

const BottomFooter = styled.div`
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  min-height: 100px; /* Reserve space to prevent CLS */

  ${theme.media.md} {
    padding: ${theme.spacing[6]} ${theme.spacing[6]};
    min-height: 80px;
  }
`;

const BottomContainer = styled.div`
  max-width: ${theme.containerWidths['2xl']};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  align-items: center;

  ${theme.media.md} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.p`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.neutral[500]};
  text-align: center;

  a {
    color: ${theme.colors.neutral[400]};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.primary[400]};
    }
  }

  ${theme.media.md} {
    text-align: left;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-height: 48px; /* Reserve space to prevent CLS */
  min-width: 200px; /* Reserve horizontal space for social icons */
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.neutral[800]};
  color: ${({ $brandColor }) => $brandColor || theme.colors.neutral[400]};
  transition: ${theme.transitions.button};

  &:hover {
    background: ${({ $brandColor }) => $brandColor || theme.colors.primary[500]};
    color: ${theme.colors.neutral[0]};
    transform: translateY(-2px);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${theme.spacing[4]};

  ${theme.media.md} {
    gap: ${theme.spacing[6]};
  }
`;

const LegalLink = styled(Link)`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.neutral[500]};
  text-decoration: none;
  transition: ${theme.transitions.default};

  &:hover {
    color: ${theme.colors.primary[400]};
  }
`;

// ============ Component ============

const FooterNew = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ social_links: {}, contact: {} });

  // Fetch site settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/settings/site/');
        if (response.ok) {
          const data = await response.json();
          setSiteSettings(data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/subscribe/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: new URLSearchParams({
          nl_email: email,
        }),
      });

      if (response.ok) {
        setStatus({ type: 'success', message: 'Thanks for subscribing!' });
        setEmail('');
      } else {
        setStatus({ type: 'error', message: 'Failed to subscribe. Please try again.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const exploreLinks = [
    { to: '/all-listings', label: 'Businesses', icon: Building2 },
    { to: '/deals-list', label: 'Deals', icon: Tag },
    { to: '/jobs-list', label: 'Jobs', icon: Briefcase },
    { to: '/classifieds', label: 'Classifieds', icon: Tag },
    { to: '/ai-tools', label: 'AI Tools', icon: Sparkles },
    { to: '/learning-hub', label: 'Learning Hub', icon: BookOpen },
  ];

  const companyLinks = [
    { to: '/blog', label: 'Blog' },
    { to: '/how-it-works', label: 'How It Works' },
  ];

  const legalLinks = [
    { to: '/terms-of-service', label: 'Terms of Service' },
    { to: '/privacy-policy', label: 'Privacy Policy' },
    { to: '/terms-and-conditions', label: 'Terms & Conditions' },
  ];

  // Social media config with brand colors
  const socialConfig = {
    facebook: { icon: Facebook, label: 'Facebook', color: '#1877F2' },
    instagram: { icon: Instagram, label: 'Instagram', color: '#E4405F' },
    twitter: { icon: Twitter, label: 'Twitter', color: '#1DA1F2' },
    linkedin: { icon: Linkedin, label: 'LinkedIn', color: '#0A66C2' },
    youtube: { icon: Youtube, label: 'YouTube', color: '#FF0000' },
    tiktok: { icon: TikTok, label: 'TikTok', color: '#000000' },
    pinterest: { icon: Pinterest, label: 'Pinterest', color: '#E60023' },
  };

  // Build social links from settings
  const socialLinks = Object.entries(siteSettings.social_links || {})
    .filter(([key, url]) => url && url.trim() !== '')
    .map(([key, url]) => ({
      href: url,
      ...socialConfig[key],
    }))
    .filter(link => link.icon); // Only include links with valid icons

  return (
    <FooterWrapper>
      {/* Newsletter Section */}
      <NewsletterSection>
        <NewsletterContainer>
          <NewsletterTitle>Stay Updated</NewsletterTitle>
          <NewsletterSubtitle>
            Get the latest listings, deals, and AI tools delivered to your inbox.
          </NewsletterSubtitle>

          <NewsletterForm onSubmit={handleNewsletterSubmit}>
            <NewsletterInput
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <NewsletterButton
              type="submit"
              disabled={isSubmitting}
              leftIcon={<Send size={18} />}
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </NewsletterButton>
          </NewsletterForm>

          {status.type && (
            <NewsletterMessage $type={status.type}>
              {status.type === 'success' ? <CheckCircle /> : <AlertCircle />}
              {status.message}
            </NewsletterMessage>
          )}
        </NewsletterContainer>
      </NewsletterSection>

      {/* Main Footer */}
      <MainFooter>
        <FooterContainer>
          <FooterGrid>
            {/* Brand Column */}
            <FooterBrand>
              <FooterLogo to="/">
                <img
                  src={`${process.env.PUBLIC_URL}/static/images/df_logo.svg`}
                  alt="Desi Firms"
                  width="160"
                  height="56"
                />
              </FooterLogo>
              <BrandDescription>
                Discover and connect with Desi businesses across Australia.
                Find listings, deals, jobs, classifieds and powerful AI tools.
              </BrandDescription>
              <ContactList>
                <ContactItem href="mailto:support@desifirms.com.au">
                  <Mail />
                  support@desifirms.com.au
                </ContactItem>
              </ContactList>
            </FooterBrand>

            {/* Explore Column */}
            <FooterColumn>
              <ColumnTitle>Explore</ColumnTitle>
              <FooterLinks>
                {exploreLinks.map((link) => (
                  <li key={link.to}>
                    <FooterLink to={link.to}>
                      {link.label}
                    </FooterLink>
                  </li>
                ))}
              </FooterLinks>
            </FooterColumn>

            {/* Company Column */}
            <FooterColumn>
              <ColumnTitle>Company</ColumnTitle>
              <FooterLinks>
                {companyLinks.map((link) => (
                  <li key={link.to}>
                    <FooterLink to={link.to}>{link.label}</FooterLink>
                  </li>
                ))}
              </FooterLinks>
            </FooterColumn>

            {/* Legal Column */}
            <FooterColumn>
              <ColumnTitle>Legal</ColumnTitle>
              <FooterLinks>
                {legalLinks.map((link) => (
                  <li key={link.to}>
                    <FooterLink to={link.to}>{link.label}</FooterLink>
                  </li>
                ))}
              </FooterLinks>
            </FooterColumn>
          </FooterGrid>
        </FooterContainer>
      </MainFooter>

      {/* Bottom Footer */}
      <BottomFooter>
        <BottomContainer>
          <Copyright>
            Copyright &copy; {currentYear} Desi Firms. All Rights Reserved |
            ABN: 20 608 158 407
          </Copyright>

          {/* Always render container to prevent CLS */}
          <SocialLinks>
            {socialLinks.map((social) => (
              <SocialLink
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                $brandColor={social.color}
              >
                <social.icon />
              </SocialLink>
            ))}
          </SocialLinks>

          <LegalLinks>
            {legalLinks.map((link) => (
              <LegalLink key={link.to} to={link.to}>
                {link.label}
              </LegalLink>
            ))}
          </LegalLinks>
        </BottomContainer>
      </BottomFooter>
    </FooterWrapper>
  );
};

export default FooterNew;
